import React, { useEffect, useState } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { FiStar, FiArrowRight, FiChevronDown, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import {
  HERO,
  WHY_US_INTRO,
  WHY_US_BLOCKS,
  ORBIS_DIFFERENCE,
  PARTNER_FEATURES,
  PROCESS_STEPS,
  PARTNER_TYPES,
  WHAT_WE_DO,
  TESTIMONIALS,
  TRUSTED_PARTNERS,
  FAQS,
  FORM_FIELDS,
  BUSINESS_TYPE_OPTIONS,
  FORM_CONTENT,
  AOS_CONFIG,
} from './PartnerData.js';
import './PartnerWithUs.css';

const CARD_PER_PAGE = 3;

const PartnerWithUs = () => {
  const [whyUsPage, setWhyUsPage] = useState(0);
  const totalWhyUsPages = Math.ceil(WHY_US_BLOCKS.length / CARD_PER_PAGE);
  const visibleWhyUsBlocks = WHY_US_BLOCKS.slice(
    whyUsPage * CARD_PER_PAGE,
    whyUsPage * CARD_PER_PAGE + CARD_PER_PAGE
  );

  const [openFaq, setOpenFaq] = useState(null);
  const [form, setForm] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    country: '',
    businessType: BUSINESS_TYPE_OPTIONS[0],
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    AOS.init(AOS_CONFIG);
    document.title = 'Partner With Us | Orbis Travel Solutions';
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Hook this up to your backend / CRM / email service
    setSubmitted(true);
  };

  return (
    <main className="partner-page">
      {/* Hero */}
      <section className="pw-hero" style={{ backgroundImage: `url(${HERO.image})` }}>
        <div className="pw-hero__overlay" />
        <div className="container pw-hero__content">
          <h1 className="pw-hero__heading" data-aos="fade-up" data-aos-delay="100">
            {HERO.heading}
          </h1>
          <p className="pw-hero__subheading" data-aos="fade-up" data-aos-delay="300">
            {HERO.subheading}
          </p>
          <a href={HERO.cta.href} className="btn btn-primary" data-aos="fade-up" data-aos-delay="400">
            {HERO.cta.label} <FiArrowRight />
          </a>
        </div>
      </section>

      {/* Why Us — narrative */}
      <section className="section pw-why-us">
        <div className="container">
          <div className="pw-intro">
            <span className="eyebrow">{WHY_US_INTRO.eyebrow}</span>
            <h2 className="pw-heading">{WHY_US_INTRO.heading}</h2>
            <p className="pw-why-us__lead">{WHY_US_INTRO.paragraph}</p>
          </div>

          <div className="pw-why-us__carousel">
            <button
              className="pw-why-us__nav pw-why-us__nav--prev"
              onClick={() => setWhyUsPage((p) => Math.max(0, p - 1))}
              disabled={whyUsPage === 0}
              aria-label="Previous"
            >
              <FiChevronLeft />
            </button>

            <div className="pw-why-us__grid">
              {visibleWhyUsBlocks.map((block) => {
                const Icon = block.icon;
                return (
                  <div key={block.id} className="pw-why-us__card">
                    <div className="pw-why-us__icon-wrap">
                      <Icon className="pw-why-us__icon" />
                    </div>
                    <h3 className="pw-why-us__title">{block.title}</h3>
                    <p className="pw-why-us__desc">{block.paragraph}</p>
                  </div>
                );
              })}
            </div>

            <button
              className="pw-why-us__nav pw-why-us__nav--next"
              onClick={() => setWhyUsPage((p) => Math.min(totalWhyUsPages - 1, p + 1))}
              disabled={whyUsPage >= totalWhyUsPages - 1}
              aria-label="Next"
            >
              <FiChevronRight />
            </button>
          </div>

          <div className="pw-why-us__dots">
            {Array.from({ length: totalWhyUsPages }).map((_, i) => (
              <button
                key={i}
                className={`pw-why-us__dot ${i === whyUsPage ? 'is-active' : ''}`}
                onClick={() => setWhyUsPage(i)}
                aria-label={`Go to page ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Orbis Difference banner */}
      <section className="pw-difference">
        <div className="container pw-difference__inner" data-aos="fade-up">
          <h2 className="pw-difference__heading">{ORBIS_DIFFERENCE.heading}</h2>
          <p className="pw-difference__paragraph">{ORBIS_DIFFERENCE.paragraph}</p>
        </div>
      </section>

      {/* Why Partner With Orbis — feature cards */}
      <section className="section pw-features">
        <div className="container">
          <div className="pw-intro" data-aos="fade-up">
            <span className="eyebrow">Why Partner With Orbis</span>
            <h2 className="pw-heading">Everything You Need in One Partner</h2>
          </div>
          <div className="pw-features__grid">
            {PARTNER_FEATURES.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={feature.id}
                  className="pw-features__card"
                  data-aos="fade-up"
                  data-aos-delay={(index % 3) * 120}
                >
                  <div className="pw-features__icon-wrap">
                    <Icon className="pw-features__icon" />
                  </div>
                  <h3 className="pw-features__title">{feature.title}</h3>
                  <p className="pw-features__desc">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Partnership Process */}
      <section className="section pw-process">
        <div className="container">
          <div className="pw-intro" data-aos="fade-up">
            <span className="eyebrow eyebrow--light">How It Works</span>
            <h2 className="pw-heading pw-heading--light">Our Partnership Process</h2>
          </div>
          <div className="pw-process__track">
            {PROCESS_STEPS.map((step, index) => {
              const Icon = step.icon;
              return (
                <div
                  key={step.id}
                  className="pw-process__step"
                  data-aos="fade-up"
                  data-aos-delay={index * 120}
                >
                  <div className="pw-process__icon-wrap">
                    <Icon className="pw-process__icon" />
                  </div>
                  <span className="pw-process__number">{String(step.id).padStart(2, '0')}</span>
                  <h4>{step.title}</h4>
                  <p>{step.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Who Can Partner With Us */}
      <section className="section pw-types">
        <div className="container">
          <div className="pw-intro" data-aos="fade-up">
            <span className="eyebrow">Who Can Partner With Us</span>
            <h2 className="pw-heading">Built For Every Kind of Travel Business</h2>
          </div>
          <div className="pw-types__grid">
            {PARTNER_TYPES.map((type, index) => {
              const Icon = type.icon;
              return (
                <div
                  key={type.id}
                  className="pw-types__card"
                  data-aos="zoom-in"
                  data-aos-delay={(index % 4) * 100}
                >
                  <Icon className="pw-types__icon" />
                  <h4>{type.title}</h4>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Experiences We Create */}
      <section className="section pw-what-we-do">
        <div className="container">
          <div className="pw-intro" data-aos="fade-up">
            <span className="eyebrow">{WHAT_WE_DO.eyebrow}</span>
            <h2 className="pw-heading">{WHAT_WE_DO.heading}</h2>
          </div>
          <div className="pw-what-we-do__grid">
            {WHAT_WE_DO.items.map((item, index) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.id}
                  className="pw-what-we-do__card"
                  data-aos="fade-up"
                  data-aos-delay={(index % 3) * 120}
                >
                  <div className="pw-what-we-do__icon-wrap">
                    <Icon className="pw-what-we-do__icon" />
                  </div>
                  <h3 className="pw-what-we-do__title">{item.title}</h3>
                  <p className="pw-what-we-do__desc">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section pw-testimonials">
        <div className="container">
          <div className="pw-intro" data-aos="fade-up">
            <span className="eyebrow">Partner Testimonials</span>
            <h2 className="pw-heading">What Our Partners Say</h2>
          </div>
          <div className="pw-testimonials__grid">
            {TESTIMONIALS.map((item, index) => (
              <div
                key={item.id}
                className="pw-testimonials__card"
                data-aos="fade-up"
                data-aos-delay={index * 150}
              >
                <div className="pw-testimonials__stars">
                  {Array.from({ length: item.rating }).map((_, i) => (
                    <FiStar key={i} className="pw-testimonials__star" />
                  ))}
                </div>
                <p className="pw-testimonials__review">&ldquo;{item.review}&rdquo;</p>
                <span className="pw-testimonials__name">{item.name}</span>
                <span className="pw-testimonials__role">{item.role}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trusted Partners marquee */}
      <section className="pw-marquee-section">
        <div className="pw-marquee">
          <div className="pw-marquee__track">
            {[...TRUSTED_PARTNERS, ...TRUSTED_PARTNERS].map((badge, index) => (
              <span className="pw-marquee__badge" key={`${badge}-${index}`}>
                {badge}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section pw-faq">
        <div className="container pw-faq__wrap">
          <div className="pw-intro">
            <span className="eyebrow">FAQ</span>
            <h2 className="pw-heading">Common Questions</h2>
          </div>
          <div className="pw-faq__list">
            {FAQS.map((faq) => (
              <div
                key={faq.id}
                className={`pw-faq__item ${openFaq === faq.id ? 'is-open' : ''}`}
              >
                <button
                  className="pw-faq__question"
                  onClick={() => setOpenFaq(openFaq === faq.id ? null : faq.id)}
                  aria-expanded={openFaq === faq.id}
                >
                  {faq.question}
                  <FiChevronDown className="pw-faq__chevron" />
                </button>
                <div className="pw-faq__answer">
                  <p>{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact / Partner Form */}
      <section className="section pw-form-section" id="partner-form">
        <div className="container pw-form__wrap" data-aos="fade-up">
          <div className="pw-intro">
            <span className="eyebrow">{FORM_CONTENT.eyebrow}</span>
            <h2 className="pw-heading">{FORM_CONTENT.heading}</h2>
            <p className="pw-form__subheading">{FORM_CONTENT.subheading}</p>
          </div>

          <form className="pw-form" onSubmit={handleSubmit}>
            <div className="pw-form__grid">
              {FORM_FIELDS.map((field) => (
                <div className="pw-field" key={field.id}>
                  <label htmlFor={field.id}>{field.label}</label>
                  <input
                    id={field.id}
                    name={field.id}
                    type={field.type}
                    required={field.required}
                    value={form[field.id]}
                    onChange={handleChange}
                  />
                </div>
              ))}

              <div className="pw-field">
                <label htmlFor="businessType">Business Type</label>
                <select
                  id="businessType"
                  name="businessType"
                  value={form.businessType}
                  onChange={handleChange}
                >
                  {BUSINESS_TYPE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="pw-field">
              <label htmlFor="message">Message</label>
              <textarea
                id="message"
                name="message"
                rows="4"
                required
                value={form.message}
                onChange={handleChange}
              />
            </div>

            <button type="submit" className="btn btn-primary pw-form__submit">
              {FORM_CONTENT.submitLabel}
            </button>

            {submitted && (
              <p className="pw-form__success">Thank you — our partnerships team will reach out shortly.</p>
            )}
          </form>
        </div>
      </section>
    </main>
  );
};

export default PartnerWithUs;